/**
 * Sneaking movement (Phase 57.5): an infiltrator on a planet that is not alert
 * walks where the guards it knows about cannot perceive it, and toward its
 * objective when it can do both. If every reachable spot is exposed and the
 * one it stands on is not, it waits for the patrol to turn.
 *
 * The bot uses the same perception geometry the guards use (range by their
 * planet's alert level, a 120° cone when they have a facing, terrain line of
 * sight), applied to what it knows: enemies it can see now (with their facing)
 * and ghosts (last seen position, no facing — treated as looking everywhere).
 * Pure; no RNG.
 */

import type { PlayerState, GameState, BotFogContext, BotSituation, BotConfig, Vec3 } from '../../types';
import { alertLayerActive, alertLevelAt, isGuard, stalledTurns } from '../../core/perception';
import { tuningFor } from './tuning';
import { teamLeads } from '../../core/modes';
import type { BotMoveResult } from './types';
import { vecNormalize, vecDot } from '../../math';
import { eyePointAt } from '../../core/los';
import { generateCandidateDirections } from './common';
import { knownGuardsOn, exposureAt } from './exposure';
export { knownGuardsOn, exposureAt, type KnownGuard } from './exposure';
import { getBotSamePlanetObjectiveDir } from '../botModeAI';
import { clampMoveTarget } from '../../core/actions';
import { dtan } from '../../core/dmath';

/**
 * Is this unit sneaking *now*: the stance is on, the alert layer exists, it is not a guard, its planet is not
 * alert — and the clock has not run out (with a match turn limit, fewer than `goLoudRoundsLeft` rounds left
 * means it is time to assault). The one predicate the walk, the jump and the plan filter all use.
 */
export function sneakingNow(state: GameState, player: PlayerState, config: BotConfig): boolean {
  const st = tuningFor(config).stealth;
  if (!st.sneak || !alertLayerActive(state) || isGuard(state, player)) return false;
  if (alertLevelAt(state, player.planetIndex) === 'alert') return false;
  const limit = state.config.modeConfig.turnLimit;
  if (limit !== undefined && st.goLoudRoundsLeft > 0 && limit - state.turnNumber < st.goLoudRoundsLeft) return false;
  // 57.7c: the objective-aware clock — quiet progress has stalled and the team is not leading: go loud
  if (st.goLoudStalledTurns > 0 && stalledTurns(state, player.teamIndex) >= st.goLoudStalledTurns && !teamLeads(state, player.teamIndex)) return false;
  return true;
}

/**
 * 57.10c: whether the sneaking walk applies here — sneaking, and at least one known guard on this planet.
 * With nobody to hide from (home with the core, an empty planet) the normal walk does the job: a sneaking
 * carrier at home used to stand still for the rest of the match because the sneaking walk had no objective there.
 */
export function sneakWalkApplies(player: PlayerState, state: GameState, config: BotConfig, fog?: BotFogContext): boolean {
  return sneakingNow(state, player, config) && knownGuardsOn(player, state, player.planetIndex, fog).length > 0;
}

/**
 * Where to walk while sneaking: the least exposed reachable spot, nudged toward the
 * objective on this planet. Null = stay put (the current spot is at least as safe as
 * anywhere it could walk, or there is nothing to walk for).
 */
export function computeSneakMove(player: PlayerState, state: GameState, sit: BotSituation, fog?: BotFogContext): BotMoveResult | null {
  const planet = state.planets[player.planetIndex];
  if (!planet || player.movementRemaining < 0.5) return null;
  const guards = knownGuardsOn(player, state, player.planetIndex, fog);
  const objective = sit.objectiveOnThisPlanet ? getBotSamePlanetObjectiveDir(player, state) : null;
  const objDir = objective ? vecNormalize(objective.surfaceDir) : null;

  // Candidates: a ring at the walk's reach, the objective (clamped to reach), and standing still
  const arc = Math.min(player.movementRemaining, 3) / Math.max(1, planet.radius);
  const ring = generateCandidateDirections(player.surfaceDir, 12, dtan(arc));
  const candidates: Array<{ dir: Vec3; stay: boolean }> = ring.map(dir => ({ dir, stay: false }));
  if (objDir) {
    const clamped = clampMoveTarget(player, planet, objDir, state.carryables, state.config.modeConfig);
    // 57.10 — the dash: when nobody is watching the objective right now, go for it, whatever the path costs.
    // (The pickup happens next turn or this one; waiting for a perfectly quiet path never ends.)
    const objExposure = exposureAt(eyePointAt(planet, objDir), guards, state.planets);
    if (clamped && objExposure <= 0.5) return { targetDir: vecNormalize(clamped) };
    if (clamped) candidates.push({ dir: vecNormalize(clamped), stay: false });
  }
  candidates.push({ dir: vecNormalize(player.surfaceDir), stay: true });

  let best: { dir: Vec3; stay: boolean } | null = null;
  let bestScore = -Infinity;
  for (const c of candidates) {
    const eye = eyePointAt(planet, c.dir);
    const exposure = exposureAt(eye, guards, state.planets);
    let score = -4 * exposure;
    if (objDir) score += 2 * vecDot(c.dir, objDir);            // progress toward the objective
    if (c.stay) score += 0.05;                                   // ties go to not moving (no noise, no commitment)
    if (score > bestScore) { bestScore = score; best = c; }
  }
  if (!best || best.stay) return null;
  return { targetDir: best.dir };
}
